import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { WorkshopsMetaData } from '@thd-olt-component-react/metadata';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  extend,
  params,
  string as stringType,
  shape,
  arrayOf as arrayOfType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { WorkshopsCard } from '../../internal-components/WorkshopsCard';
import { RegistrationModal } from '../../internal-components/RegistrationModal';
import { kidsStaticCards, virtualStaticCards } from './static-cards-data.json';
import styles from '../../styles/available-events.module.scss';

const cx = classNames.bind(styles);

const AvailableEvents = () => {
  const experienceContext = useContext(ExperienceContext);
  const { isDebugMode, customer } = experienceContext;
  const [modalOpen, setModalOpen] = useState(false);

  const store = useStore({ varnish: false });

  const workshopsMetaData = {
    alternateUrlEnCa: 'https://www.homedepot.ca/workshops',
    alternateUrlFrCa: 'https://www.homedepot.ca/ateliers',
    alternateUrlEsMx: 'https://www.homedepot.com.mx/talleres-hazlo-tu-mismo',
    data: {
      canonicalURL: '/workshops/',
      description: 'The Home Depot offers free do-it-yourself workshops for all ages and '
        + 'experience levels. Discover livestream and digital project how-tos for kids, '
        + 'Homeowner 101 and other DIY workshops.',
      siteName: 'The Home Depot',
      fbAdmins: '100005416960152',
      title: 'DIY Workshops | The Home Depot',
      type: 'Workshops'
    },
    title: 'DIY Workshops | The Home Depot',
    description: 'The Home Depot offers free do-it-yourself workshops for all ages and '
      + 'experience levels. Discover livestream and digital project how-tos for kids, '
      + 'Homeowner 101 and other DIY workshops.'
  };

  const { data } = useDataModel('workshops', {
    variables: { storeId: store?.storeId },
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
        'nucleus-exp-workshops.page-load',
        { ...workshopsMetaData }
      );
    }
  }, []);

  const openRegisterModal = () => {
    setModalOpen(true);
  };

  const closeRegisterModal = () => {
    setModalOpen(false);
  };

  return (
    <LayoutManager
      name="available-events-page"
      input={{ isDebugMode }}
      useContextForProps
    >
      <RegistrationModal
        modalOpen={modalOpen}
        storeId={store?.storeId}
        workshopsData={data}
        modalOnClose={closeRegisterModal}
        svocId={customer.svocId}
      />
      <LayoutGroup name="metadata">
        <WorkshopsMetaData
          alternateUrlEnCa={workshopsMetaData.alternateUrlEnCa}
          alternateUrlFrCa={workshopsMetaData.alternateUrlFrCa}
          alternateUrlEsMx={workshopsMetaData.alternateUrlEsMx}
          data={workshopsMetaData.data}
          description={workshopsMetaData.description}
          title={workshopsMetaData.title}
        />
      </LayoutGroup>
      <LayoutGroup
        name="available-events-content"
        className={cx('isBound', 'available-events')}
      >
        <div className="sui-m-4 lg:sui-m-24">
          <div className="sui-grid sui-gap-6">
            <div className="sui-col-span-12">
              <Typography variant="h1" weight="condensed">Workshops</Typography>
            </div>
            <div className="sui-col-span-12">
              <Typography variant="body-lg" weight="regular" height="tight">
                With The Home Depot’s free, interactive livestream workshops, our associates can help
                you take on projects, teach you how to care for different areas of your home, plus so
                much more. See what’s available and register today.
              </Typography>
            </div>
          </div>
          <div className="sui-py-10">
            <Typography variant="h3" weight="bold" height="tight">
              Virtual Workshops
            </Typography>
          </div>
          <div className="sui-grid sm:sui-grid-cols-1 md:sui-grid-cols-2 lg:sui-grid-cols-4 sui-gap-4">
            {virtualStaticCards.map((cardData) => {
              const {
                id, name, imageAlt, imagePath, logoAlt, logoPath,
                callToActionButtonText, callToActionUrl, description
              } = cardData;

              return (
                <WorkshopsCard
                  key={id}
                  name={name}
                  imageAlt={imageAlt}
                  imagePath={imagePath}
                  logoAlt={logoAlt}
                  logoPath={logoPath}
                  callToActionButtonText={callToActionButtonText}
                  callToActionUrl={callToActionUrl}
                  description={description}
                />
              );
            })}
          </div>
          <div className="sui-py-10">
            <Typography variant="h3" weight="bold" height="tight">
              Kids Workshops
            </Typography>
          </div>
          <div className="sui-grid sm:sui-grid-cols-1 md:sui-grid-cols-2 lg:sui-grid-cols-4 sui-gap-4">
            {kidsStaticCards.map((cardData) => {
              const {
                id, name, imageAlt, imagePath, logoAlt, logoPath,
                callToActionButtonText, callToActionUrl, description
              } = cardData;

              return (
                <WorkshopsCard
                  key={id}
                  name={name}
                  imageAlt={imageAlt}
                  imagePath={imagePath}
                  logoAlt={logoAlt}
                  logoPath={logoPath}
                  callToActionButtonText={callToActionButtonText}
                  callToActionUrl={callToActionUrl}
                  description={description}
                  openRegisterModal={openRegisterModal}
                />
              );
            })}
          </div>
        </div>
      </LayoutGroup>
    </LayoutManager>
  );
};

AvailableEvents.displayName = 'AvailableEvents';
AvailableEvents.propTypes = {};
AvailableEvents.defaultProps = {};
AvailableEvents.dataModel = extend(
  {
    workshops: params({ storeId: stringType().isRequired() }).shape({
      id: stringType(),
      storeId: stringType(),
      startDate: stringType(),
      endDate: stringType(),
      name: stringType(),
      description: stringType(),
      imagePath: stringType(),
      type: stringType(),
    }),
    storeSearch: params({
      storeSearchInput: stringType(),
      pagesize: stringType(),
    }).shape({
      stores: arrayOfType(shape({
        storeId: stringType(),
        name: stringType(),
        address: shape({
          street: stringType(),
          city: stringType(),
          state: stringType(),
          postalCode: stringType(),
          country: stringType()
        }),
        phone: stringType(),
      })),
    })
  },
);

export { AvailableEvents };
